import type { ReactNode } from 'react'
import { Button, Modal } from '@systemeio/components'
import { XCloseIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { DataFile } from 'shared/types/datafile-type'
import { useCoursesOfCourseBundle } from 'modules/school/hooks/use-courses-of-course-bundle'
import { SchoolEnrollmentId } from '../../types/school-enrollments-interface'
import { CourseCard } from './course-card'
import { CourseCardsSkeleton } from './course-card-skeleton'

const DEFAULT_COURSE_COVER = '/images/default-course-cover.png'

export interface SchoolCourseBundleModalModalProps {
  isOpened: boolean
  onClose: () => void
  schoolEnrollmentId: SchoolEnrollmentId | null
  name: string | null
  imagePath: DataFile['path'] | null
}

function SchoolCourseBundleModal({
  isOpened,
  onClose,
  schoolEnrollmentId,
  name,
  imagePath,
}: SchoolCourseBundleModalModalProps): ReactNode {
  const { t } = useTranslation()

  const { coursesOfCourseBundle } = useCoursesOfCourseBundle({ schoolEnrollmentId })

  return (
    <Modal.Root
      open={isOpened}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <Modal.Popup
        showCloseButton={false}
        className="my-auto max-h-none min-h-0 w-full max-w-[min(56rem,calc(100vw-2rem))] overflow-visible max-sm:h-dvh max-sm:max-h-dvh max-sm:max-w-none max-sm:overflow-hidden max-sm:rounded-none"
        viewportProps={{
          className:
            'overflow-hidden p-0 sm:flex sm:items-start sm:justify-center sm:overflow-y-auto sm:p-4',
        }}
        aria-label={name ?? undefined}
      >
        <Modal.Close
          render={
            <Button
              variant="ghost-gray"
              size="icon-sm"
              aria-label={t('confirmation_modal.cancel')}
              className="absolute top-2 right-2 z-10 rounded-full border-none! bg-bg-primary! p-2 text-text-primary hover:bg-bg-primary!"
            />
          }
        >
          <XCloseIcon aria-hidden="true" className="size-4" />
        </Modal.Close>
        <div
          data-testid="bundle-modal-content"
          className="scrollbar-hide min-h-0 flex-1 overflow-y-auto"
        >
          <div
            data-testid="bundle-modal-hero-frame"
            className="relative overflow-hidden rounded-t-2xl before:absolute before:inset-0 before:rounded-t-2xl before:bg-linear-to-b before:from-transparent before:to-[rgb(var(--text-primary)/1)]"
          >
            <div className="relative min-h-50 w-full sm:h-75">
              <img
                data-testid="bundle-modal-hero-image"
                className="absolute inset-0 block size-full object-cover object-center"
                src={imagePath || DEFAULT_COURSE_COVER}
                alt=""
              />
            </div>
            <h3 className="absolute bottom-3 left-3 text-xl font-bold text-text-on sm:bottom-8 sm:left-8 sm:text-2xl">
              {name}
            </h3>
          </div>
          <div className="p-5 md:p-8">
            <div className="flex justify-between">
              <span className="text-lg font-bold text-text-primary sm:text-3xl">
                {t('school.course_bundle_modal.courses_label')}
              </span>
            </div>
            <div className={`mt-4 flex flex-col gap-12 sm:mt-9`}>
              {coursesOfCourseBundle ? (
                coursesOfCourseBundle?.map((data, index) => <CourseCard key={index} data={data} />)
              ) : (
                <CourseCardsSkeleton />
              )}
            </div>
          </div>
        </div>
      </Modal.Popup>
    </Modal.Root>
  )
}

export default SchoolCourseBundleModal
